import {Injector, runInInjectionContext, Injectable} from '@angular/core'
import {UserService} from "../core/user/user.service"
import {nanoid} from 'nanoid'
import {AngularFirestore} from '@angular/fire/compat/firestore'
import { HttpClient } from "@angular/common/http"
import {environment} from "environments/environment"
import Bugsnag from '@bugsnag/js'
import {BehaviorSubject} from "rxjs"
import {DeepObjectTransformer} from 'app/utils/DeepObjectTransformer'

export enum eventType {

  displayPublication = 'display-publication',

  extUpdateValue = 'ext-update-value',
  extAllCollected = 'ext-all-collected',

  landingReadyToReply = 'landing-ready-to-reply',

  landingScreenOut = 'landing-screen-out',
  landingComplete = 'landing-complete', // <--- = on a répondu à tous les sondages !

  surveyStartReply = 'survey-startReply',
  surveyReplied = 'survey-replied',

  cintCompleteRedirect = 'cint-complete-redirect',
  cintScreenoutRedirect = 'cint-screenout-redirect',

  publicCompleteRedirect = 'public-complete-redirect',
  publicScreenoutRedirect = 'public-screenout-redirect',

  captchaStart = 'captcha-start',
  captchaPassed = 'captcha-passed',
  captchaError = 'captcha-error',


}

@Injectable({
  providedIn: 'root'
})
export class EventMessagesService {

  hasPendingRequest$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)

  BASE_URL = environment.apiUrl

  uniqSession = nanoid(22)

  eventStack: any[] = []
  eventStackIndex: string[] = []

  pubUuid: string
  surveyId: number

  public browserName = '';
  public browserVersion = '';

  constructor(
    private _httpClient: HttpClient,
    private userService: UserService,
    private db: AngularFirestore,
    private injector: Injector,
  ) {
    this.browserName = this.getBrowserName();
    this.browserVersion = this.getBrowserVersion();
  }

  public setPubUuid(data: string) {
    this.pubUuid = data
  }

  public setSurveyId(data: number) {
    this.surveyId = data
  }

  emitEvent(eventName: eventType, data: any = null) {

    //
    //
    //
    const uniqEventCollect: eventType[] = [
      eventType.displayPublication,
      eventType.extAllCollected,
      eventType.cintCompleteRedirect,
      eventType.landingComplete, // <-- ?
    ]
    //
    //
    //

    const event = {

      // ID: `${eventName}-${nanoid(12)}`,

      uniqSession: this.uniqSession,
      dateTime: new Date().toISOString(),

      name: eventName,
      data,

      userId: this.userService.user$.value?.id || 'No user ID ?',
      rid: this.userService.user$.value?.publicStatus?.rid || false,

      user: this.userService?.user$.value || {},

      pubUuid: this.pubUuid || false,

      surveyId: this.surveyId || false,

      browser: `${this.browserName} (${this.browserVersion})`,

    }

    //
    // On ne va capturer qu'une seule fois certains évènements...
    //

    // console.log(`-=> Event [${eventName}] **`, event);
    let collect = true
    if (uniqEventCollect.includes(eventName)) {

      if (!this.eventStackIndex.includes(eventName)) {
        this.eventStackIndex.push(eventName)
      } else {
        collect = false
      }

    }

    //
    // On collecte ou pas ?
    //
    if (collect) {

      this.eventStack.push(event)

      this.hasPendingRequest$.next(true)

      runInInjectionContext(this.injector, () => {
        const eventWithoutUndefinedValues = new DeepObjectTransformer().removeUndefinedAndNullValues(event)

        const remoteLogsRef = this.db.collection(`${environment.firestoreLogCollection}/${this.pubUuid}/${this.uniqSession}`)

        remoteLogsRef.add(eventWithoutUndefinedValues)
          .then(() => runInInjectionContext(this.injector, () => this.hasPendingRequest$.next(false)))
          .catch(err => runInInjectionContext(this.injector, () => {
            this.hasPendingRequest$.next(false)
            Bugsnag.notify(err, (event) => {
              event.addMetadata('Event Messages Service', {
                method: this.emitEvent.name,
                payload: eventWithoutUndefinedValues,
                firestoreCollection: environment.firestoreLogCollection
              })
            })
          }))

        if (this.pubUuid) {
          this.db.collection(environment.firestoreLogCollection)
            .doc(this.pubUuid)
            .set({maxEventDate: new Date()}, {merge: true})
            .then()
            .catch(err => runInInjectionContext(this.injector, () => {
              Bugsnag.notify(err, (event) => {
                event.addMetadata('[pubUuid] Event Messages Service', {
                  method: this.emitEvent.name,
                  payload: eventWithoutUndefinedValues,
                  firestoreCollection: environment.firestoreLogCollection
                })
              })
            }))
        }

        this.callAWSBackend({
          uniqSession: this.uniqSession,
          eventWithoutUndefinedValues,
        })
      })
    }

  }


  callAWSBackend(params) {
    const endpoint = `${this.BASE_URL}/appCommunity/publicActions`
    const action = 'logEvent'
    const body = {
      action,
      params,
    }
    this._httpClient.post(endpoint, body)
      .subscribe({
        next: (response: any) => {
          //
        },
        error: err => {
          Bugsnag.notify(err, (event) => {
            event.addMetadata('callAWSBackend', {
              method: 'callAWSBackend',
              body,
              endpoint
            })
          })
        }
      })

  }

  getBrowserName() {
    const agent = window.navigator.userAgent.toLowerCase();
    switch (true) {
      case agent.indexOf('edge') > -1:
        return 'edge';
      case agent.indexOf('opr') > -1 && !!(window as any).opr:
        return 'opera';
      case agent.indexOf('chrome') > -1 && !!(window as any).chrome:
        return 'chrome';
      case agent.indexOf('trident') > -1:
        return 'ie';
      case agent.indexOf('firefox') > -1:
        return 'firefox';
      case agent.indexOf('safari') > -1:
        return 'safari';
      default:
        return 'other';
    }
  }

  getBrowserVersion() {
    const userAgent: any = navigator.userAgent;
    let tempMatch: any;
    let versionMatch: any =
      userAgent.match(
        /(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i
      ) || [];
    if (/trident/i.test(versionMatch[1])) {
      tempMatch = /\brv[ :]+(\d+)/g.exec(userAgent) || [];
      return 'IE ' + (tempMatch[1] || '');
    }
    if (versionMatch[1] === 'Chrome') {
      tempMatch = userAgent.match(/\b(OPR|Edge)\/(\d+)/);
      if (tempMatch != null) {
        return tempMatch.slice(1).join(' ').replace('OPR', 'Opera');
      }
    }
    versionMatch = versionMatch[2]
      ? [versionMatch[1], versionMatch[2]]
      : [navigator.appName, navigator.appVersion, '-?'];
    if ((tempMatch = userAgent.match(/version\/(\d+)/i)) != null)
      versionMatch.splice(1, 1, tempMatch[1]);
    return versionMatch;
  }


}

